import { useEffect, useRef } from 'react';
import {
  Cartesian3,
  Color,
  PolygonHierarchy,
  ColorMaterialProperty,
  HeightReference as _HeightReference,
  Entity,
  Viewer as _Viewer,
} from 'cesium';
import { useRackEditorStore, RackRow } from '../stores/rackEditorStore';
import { useViewerStore } from '../stores/viewerStore';

// Zone colors for rack visualization
const ZONE_COLORS: Record<string, string> = {
  storage: '#3498db',
  receiving: '#27ae60',
  shipping: '#e74c3c',
  staging: '#f39c12',
  office: '#9b59b6',
  default: '#95a5a6',
};

interface RackPreview3DProps {
  enabled?: boolean;
}

function RackPreview3D({ enabled = true }: RackPreview3DProps) {
  const { viewer } = useViewerStore();
  const { rackRows, selectedRackRow, selectedZone } = useRackEditorStore();
  const entitiesRef = useRef<string[]>([]);

  // Remove all rack entities
  const clearRackEntities = () => {
    if (!viewer) return;

    entitiesRef.current.forEach((entityId) => {
      const entity = viewer.entities.getById(entityId);
      if (entity) {
        viewer.entities.remove(entity);
      }
    });
    entitiesRef.current = [];
  };

  // Create a 3D box representation for a rack row
  const createRackEntity = (row: RackRow, isSelected: boolean): Entity | null => {
    if (!viewer || !row.geo_start_lat || !row.geo_start_lon || !row.geo_end_lat || !row.geo_end_lon) {
      return null;
    }

    const zoneType = selectedZone?.zone_type || 'default';
    const baseColor = ZONE_COLORS[zoneType] || ZONE_COLORS.default;
    const color = Color.fromCssColorString(baseColor).withAlpha(isSelected ? 0.8 : 0.5);

    // Calculate rack dimensions
    const totalHeightFt = (row.num_levels * row.level_height_in) / 12;
    const heightMeters = totalHeightFt * 0.3048;
    const depthFt = row.bay_depth_in / 12;
    const depthMeters = depthFt * 0.3048;

    // Calculate perpendicular offset for rack depth
    const startLat = row.geo_start_lat;
    const startLon = row.geo_start_lon;
    const endLat = row.geo_end_lat;
    const endLon = row.geo_end_lon;

    // Calculate direction vector
    const dLat = endLat - startLat;
    const dLon = endLon - startLon;

    // Perpendicular offset (approximate - works for small distances)
    const metersPerDegreeLat = 111000;
    const metersPerDegreeLon = 111000 * Math.cos((startLat * Math.PI) / 180);
    const depthDegreesLat = (depthMeters / metersPerDegreeLat) * (-dLon / Math.sqrt(dLat * dLat + dLon * dLon));
    const depthDegreesLon = (depthMeters / metersPerDegreeLon) * (dLat / Math.sqrt(dLat * dLat + dLon * dLon));

    // Create polygon for rack footprint with depth
    const positions: Cartesian3[] = [];

    if (row.has_left_side) {
      // Left side of rack
      positions.push(
        Cartesian3.fromDegrees(startLon, startLat),
        Cartesian3.fromDegrees(endLon, endLat),
        Cartesian3.fromDegrees(endLon + depthDegreesLon, endLat + depthDegreesLat),
        Cartesian3.fromDegrees(startLon + depthDegreesLon, startLat + depthDegreesLat)
      );
    }

    if (row.has_right_side) {
      // Right side of rack (if both sides, create second entity)
      if (row.has_left_side) {
        // Create second entity for right side
        const rightPositions = [
          Cartesian3.fromDegrees(startLon, startLat),
          Cartesian3.fromDegrees(endLon, endLat),
          Cartesian3.fromDegrees(endLon - depthDegreesLon, endLat - depthDegreesLat),
          Cartesian3.fromDegrees(startLon - depthDegreesLon, startLat - depthDegreesLat),
        ];

        const rightEntityId = `rack-row-right-${row.id}`;
        viewer.entities.add({
          id: rightEntityId,
          name: `Aisle ${row.aisle_number} (Right)`,
          polygon: {
            hierarchy: new PolygonHierarchy(rightPositions),
            extrudedHeight: heightMeters,
            height: 0,
            material: new ColorMaterialProperty(color),
            outline: true,
            outlineColor: isSelected ? Color.WHITE : Color.BLACK,
            outlineWidth: isSelected ? 2 : 1,
          },
        });
        entitiesRef.current.push(rightEntityId);
      } else {
        // Only right side
        positions.push(
          Cartesian3.fromDegrees(startLon, startLat),
          Cartesian3.fromDegrees(endLon, endLat),
          Cartesian3.fromDegrees(endLon - depthDegreesLon, endLat - depthDegreesLat),
          Cartesian3.fromDegrees(startLon - depthDegreesLon, startLat - depthDegreesLat)
        );
      }
    }

    if (positions.length === 0) return null;

    const entityId = `rack-row-${row.id}`;

    // Calculate total locations for this rack
    const sides = (row.has_left_side ? 1 : 0) + (row.has_right_side ? 1 : 0);
    const totalLocations = row.num_bays * row.num_levels * sides;

    const entity = viewer.entities.add({
      id: entityId,
      name: `Aisle ${row.aisle_number}${row.has_left_side && row.has_right_side ? '' : row.has_left_side ? ' (Left)' : ' (Right)'}`,
      description: `
        <table style="width:100%">
          <tr><td><b>Aisle:</b></td><td>${row.aisle_number}</td></tr>
          <tr><td><b>Bays:</b></td><td>${row.num_bays}</td></tr>
          <tr><td><b>Levels:</b></td><td>${row.num_levels}</td></tr>
          <tr><td><b>Prefix:</b></td><td>${row.location_prefix || 'N/A'}</td></tr>
          <tr><td><b>Total Locations:</b></td><td>${totalLocations}</td></tr>
          <tr><td><b>Generated:</b></td><td>${row.location_count}</td></tr>
          <tr><td><b>Dimensions:</b></td><td>${row.total_length_in ? (row.total_length_in / 12).toFixed(1) : 'N/A'} ft x ${(row.bay_depth_in / 12).toFixed(1)} ft x ${totalHeightFt.toFixed(1)} ft</td></tr>
        </table>
      `,
      polygon: {
        hierarchy: new PolygonHierarchy(positions),
        extrudedHeight: heightMeters,
        height: 0,
        material: new ColorMaterialProperty(color),
        outline: true,
        outlineColor: isSelected ? Color.WHITE : Color.BLACK,
        outlineWidth: isSelected ? 2 : 1,
      },
    });

    entitiesRef.current.push(entityId);
    return entity;
  };

  // Update rack visualization when rack rows change
  useEffect(() => {
    if (!enabled || !viewer) {
      clearRackEntities();
      return;
    }

    // Clear existing entities
    clearRackEntities();

    // Create new entities for each rack row
    rackRows.forEach((row) => {
      const isSelected = selectedRackRow?.id === row.id;
      createRackEntity(row, isSelected);
    });

    return () => {
      clearRackEntities();
    };
  }, [viewer, rackRows, selectedRackRow, selectedZone, enabled]);

  // Fly to selected rack row
  useEffect(() => {
    if (!viewer || !selectedRackRow?.geo_start_lat || !selectedRackRow?.geo_start_lon) return;

    const midLat = ((selectedRackRow.geo_start_lat || 0) + (selectedRackRow.geo_end_lat || 0)) / 2;
    const midLon = ((selectedRackRow.geo_start_lon || 0) + (selectedRackRow.geo_end_lon || 0)) / 2;

    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(midLon, midLat, 100),
      duration: 1,
    });
  }, [viewer, selectedRackRow]);

  // This component doesn't render anything itself - it just manages Cesium entities
  return null;
}

export default RackPreview3D;
